import { Badge, EmptyState, SkeletonText } from '@aurora/ui-kit';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client.js';
import { useMe } from '../auth/useAuth.js';
import { useModules } from '../modules/useModules.js';
import { useTenantSlug } from '../state/tenant.js';

/** Core-Widget: Begrüßung + aktiver Mandant. */
export function WelcomeWidget() {
  const me = useMe();
  const tenantSlug = useTenantSlug();
  const tenant = me.data?.tenants.find((entry) => entry.slug === tenantSlug);

  return (
    <div>
      <p style={{ fontSize: 'var(--au-text-xl)', fontWeight: 600 }}>
        Hallo, {me.data?.user.displayName ?? '…'}
      </p>
      <p style={{ color: 'var(--au-text-muted)', marginTop: 'var(--au-space-2)' }}>
        Aktiver Mandant: <strong>{tenant?.name ?? '–'}</strong>. Drücke{' '}
        <kbd className="au-kbd">⌘K</kbd>, um überall hinzuspringen.
      </p>
    </div>
  );
}

/** Core-Widget: Modulstatus des Mandanten. */
export function ModuleStatusWidget() {
  const modules = useModules();
  const states = modules.data?.states ?? [];

  if (states.length === 0) {
    return (
      <EmptyState
        title="Noch keine Module"
        description="Sobald Fachmodule installiert sind, erscheinen sie hier."
      />
    );
  }

  return (
    <ul className="module-status">
      {states.map((state) => (
        <li key={state.name} className="module-status__row">
          <span>{state.title ?? state.name}</span>
          {state.enabledForTenant ? (
            <Badge tone="success">aktiv</Badge>
          ) : state.enabled ? (
            <Badge tone="neutral">verfügbar</Badge>
          ) : (
            <Badge tone="warning">deaktiviert</Badge>
          )}
        </li>
      ))}
    </ul>
  );
}

interface FeedItem {
  id: string;
  name: string;
  payload: Record<string, unknown>;
  occurredAt: string;
}

/** Event-Namen → lesbare deutsche Labels für den Feed. */
const EVENT_LABELS: Record<string, string> = {
  'crm.contact.created': 'Kontakt angelegt',
  'crm.company.created': 'Firma angelegt',
  'crm.deal.created': 'Deal angelegt',
  'crm.deal.stage_changed': 'Deal verschoben',
  'crm.deal.won': 'Deal gewonnen 🎉',
  'crm.deal.lost': 'Deal verloren',
  'sales.quote.sent': 'Angebot versendet',
  'sales.quote.accepted': 'Angebot angenommen',
  'sales.quote.rejected': 'Angebot abgelehnt',
  'accounting.invoice.issued': 'Rechnung ausgestellt',
  'accounting.invoice.paid': 'Rechnung bezahlt',
  'core.tenant_module.changed': 'Modul geändert',
};

function feedTitle(item: FeedItem): string {
  const payload = item.payload;
  const candidate = payload['title'] ?? payload['name'] ?? payload['customerName'];
  return typeof candidate === 'string' ? candidate : '';
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} min`;
  const hoursAgo = Math.round(minutes / 60);
  if (hoursAgo < 24) return `vor ${hoursAgo} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}

/** Core-Widget: die letzten fachlichen Ereignisse des Mandanten. */
export function ActivityFeedWidget() {
  const feed = useQuery({
    queryKey: ['activity-feed'],
    queryFn: () => api<{ items: FeedItem[] }>('/api/v1/activity-feed'),
  });

  if (feed.isLoading) return <SkeletonText lines={4} />;
  const items = (feed.data?.items ?? []).filter((item) => EVENT_LABELS[item.name] !== undefined);

  if (items.length === 0) {
    return (
      <EmptyState
        title="Noch keine Aktivitäten"
        description="Sobald im System gearbeitet wird, erscheint hier der Verlauf."
      />
    );
  }

  return (
    <ul className="feed">
      {items.slice(0, 8).map((item) => (
        <li key={item.id} className="feed__row">
          <span className="feed__label">{EVENT_LABELS[item.name]}</span>
          <span className="feed__title">{feedTitle(item)}</span>
          <span className="feed__time">{relativeTime(item.occurredAt)}</span>
        </li>
      ))}
    </ul>
  );
}
